import { useLazyQuery, useMutation } from '@apollo/client';
import { debounce } from 'lodash';
import { MutableRefObject, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RECEIVE_EXCEPTION_NOTE_MAX_LENGTH } from '../../../../constants';
import {
  CREATE_EXCEPTION_NOTE,
  LIST_EXCEPTION_NOTES,
} from '../../../../services/receive-exception/queries';
import {
  ReceiveException,
  ReceiveExceptionNote,
} from '../../../../services/receive-exception/types';
import { isElementScrolledNearTop, isElementScrolledToBottom } from '../../../../utils';
import { transformReceiveExceptionError } from '../helper';
import './style.css';

/* pixels between scroll position and top to trigger fetch next page */
const LOAD_MORE_THRESHOLD = 200;

export const NOTES_PAGE_SIZE = 20;

export type ExceptionNoteState = {
  newNote: string;
  setNewNote: (note: string) => void;
  listNotesLoading: boolean;
  listNotesError: string;
  notes: ReceiveExceptionNote[];
  handleCreateNote: () => void;
  creatingNote: boolean;
  createNoteError: string;
  handleScroll: () => void;
  noteListRef: MutableRefObject<HTMLDivElement | null>;
  noteListContainerRef: MutableRefObject<HTMLDivElement | null>;
};

export default function useExceptionNotes(exception: ReceiveException): ExceptionNoteState {
  const { t } = useTranslation();
  /* retain the new note user provided in the input */
  const [newNote, setNewNote] = useState<string>('');
  const [notes, setNotes] = useState<ReceiveExceptionNote[]>([]);
  const [listNotesError, setListNotesError] = useState<string>('');
  const [createNoteError, setCreateNoteError] = useState<string>('');
  const [nextToken, setNextToken] = useState<string>('');
  /* flag to differentiate between first load and second load */
  const [fetchingMore, setFetchingMore] = useState<boolean>(false);
  /* dom references to the notes list */
  const noteListRef = useRef<HTMLDivElement | null>(null);
  /* dom reference to container for notes list */
  const noteListContainerRef = useRef<HTMLDivElement | null>(null);
  /* remember previous notes list element height for retaining scroll position */
  const [objectHeight, setObjectHeight] = useState<number>(0);

  /**
   * notes list is implemented as a reverse infinite scroll. On initial load the notes list
   * will be scrolled all the way to the bottom so that user can see latest notes.
   * when user scrolls up and reaches top, fetch the next page and prepend to list then update
   * scroll position to remaining in its previous note.
   */
  const updateScrollPosition = () => {
    if (noteListRef.current && noteListContainerRef.current && !fetchingMore) {
      setObjectHeight(noteListContainerRef.current.scrollHeight);
      noteListContainerRef.current.scrollTop = noteListRef.current.scrollHeight;
    }
    if (noteListRef.current && noteListContainerRef.current && fetchingMore) {
      noteListContainerRef.current.scrollTop = noteListRef.current.scrollHeight - objectHeight;
      setObjectHeight(noteListContainerRef.current.scrollHeight);
    }
  };

  const [getExceptionNotes, { loading: listNotesLoading }] = useLazyQuery(LIST_EXCEPTION_NOTES, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    onCompleted: (result) => {
      /* prepend the notes to existing notes list to add it above */
      setListNotesError('');
      setNotes((prevNotes) => [...result.listReceiveExceptionNotes.notes, ...prevNotes]);
      setNextToken(result.listReceiveExceptionNotes.nextToken);
      /* defer and update the scroll position */
      setTimeout(() => {
        updateScrollPosition();
      }, 0);
    },
    onError: (e) => {
      setListNotesError(transformReceiveExceptionError(e));
    },
  });

  const [createNote, { loading: creatingNote }] = useMutation(CREATE_EXCEPTION_NOTE, {
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    getExceptionNotes({
      variables: { request: { exceptionId: exception.exceptionId, maxResults: NOTES_PAGE_SIZE } },
    });
  }, [exception.exceptionId, getExceptionNotes]);

  const handleCreateNote = async () => {
    try {
      if (!newNote) return;
      if (newNote.length > RECEIVE_EXCEPTION_NOTE_MAX_LENGTH) {
        setCreateNoteError(
          `${t(
            'Note too large. Maximum characters allowed is'
          )} ${RECEIVE_EXCEPTION_NOTE_MAX_LENGTH}`
        );
        return;
      }

      const {
        data: { createReceiveExceptionNote: createdNote },
      } = await createNote({
        variables: { request: { exceptionId: exception.exceptionId, note: newNote } },
      });
      setNewNote('');
      setNotes((prevNotes) => [...prevNotes, createdNote]);

      // scroll to bottom after adding note at the end
      if (noteListContainerRef.current && noteListRef.current) {
        noteListContainerRef.current.scrollTop = noteListRef.current.scrollHeight;
      }
    } catch (e) {
      setCreateNoteError(transformReceiveExceptionError(e));
    }
  };

  /**
   * fetch next page and prepend on user scroll to top
   */
  const handleScroll = debounce(async () => {
    /* skip if the scroll position is not near top or if its initial position (scrolled all the way to bottom) */
    try {
      if (
        (noteListContainerRef.current &&
          (!isElementScrolledNearTop(noteListContainerRef.current, LOAD_MORE_THRESHOLD) ||
            isElementScrolledToBottom(noteListContainerRef.current))) ||
        !nextToken ||
        listNotesLoading
      ) {
        return;
      }

      setFetchingMore(true);
      await getExceptionNotes({
        variables: {
          request: {
            exceptionId: exception.exceptionId,
            nextToken,
            maxResults: 20,
          },
        },
      });
    } catch (e) {
      setListNotesError(transformReceiveExceptionError(e));
    }
  }, 100);

  return {
    newNote,
    setNewNote,
    listNotesLoading,
    listNotesError,
    notes,
    handleCreateNote,
    creatingNote,
    createNoteError,
    handleScroll,
    noteListRef,
    noteListContainerRef,
  };
}
